import { useEffect } from "react";
import { useImmer } from "use-immer";

import { IgAccordion } from "@intelligems/ig-theme/IgAccordion";
import { IgBadge } from "@intelligems/ig-theme/IgBadge";
import Grid from "@mui/material/Grid";
import {
  AnalyticFiltersConfigType,
  DashboardPageName,
  SitewideAnalyticsPageName,
} from "@src/api/types/dashboards";
import {
  filterMatchesSearch,
  getSelectedCount,
  shouldShowFilter,
} from "@src/features/analytics/analyticsFilters/utils";
import { useSelector } from "@src/store";
import { getCustomMetrics } from "@src/store/slices/experienceMetrics/experienceMetrics";

interface AnalyticsAccordionProps {
  filterButtons: AnalyticFiltersConfigType[];
  search: string;
  pageName?: DashboardPageName | SitewideAnalyticsPageName;
}

export const AnalyticsAccordion = ({
  filterButtons,
  search,
  pageName,
}: AnalyticsAccordionProps) => {
  const [expandedAccordions, setExpandedAccordions] = useImmer<string[]>([]);
  const experienceCustomMetrics = useSelector(getCustomMetrics);

  const handleAccordionToggle = (label: string) => {
    setExpandedAccordions((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label]
    );
  };

  // Update expanded accordions when search changes
  useEffect(() => {
    if (search) {
      const matchingAccordions = filterButtons
        .filter((filter) => shouldShowFilter(filter, pageName))
        .filter((filter) =>
          filterMatchesSearch(filter, search, experienceCustomMetrics)
        )
        .map((filter) => filter.label);

      setExpandedAccordions(matchingAccordions);
    } else {
      // When search is cleared, collapse all except default
      setExpandedAccordions(
        pageName === "performance" ? ["Orders to Include"] : ["Device Type"]
      );
    }
  }, [search]);

  return (
    <Grid container direction={"column"} rowSpacing={2}>
      {filterButtons
        .filter(
          (filter) =>
            shouldShowFilter(filter, pageName) &&
            filterMatchesSearch(filter, search, experienceCustomMetrics)
        )
        .map(
          (filter) =>
            shouldShowFilter(filter, pageName) && (
              <Grid key={filter.label}>
                <IgAccordion
                  endHeaderIcon={
                    <Grid mt={-1} ml={3}>
                      <IgBadge
                        badgeContent={getSelectedCount(filter)}
                        color={"accent"}
                      />
                    </Grid>
                  }
                  expanded={expandedAccordions.includes(filter.label)}
                  onChange={() => handleAccordionToggle(filter.label)}
                  description={filter.settings[0].description}
                  customDescription={
                    filter.settings[0].customDescription ? (
                      <Grid
                        sx={{
                          display: !expandedAccordions.includes(filter.label)
                            ? "none"
                            : "flex",
                        }}
                      >
                        {filter.settings[0].customDescription}
                      </Grid>
                    ) : undefined
                  }
                  title={filter.label}
                >
                  <Grid container direction={"column"} padding={0}>
                    {filter.settings.map((setting) => (
                      <Grid
                        container
                        direction={filter.isSwitch ? "row" : "column"}
                        key={setting.key}
                        alignItems={
                          filter.isSwitch && filter.hasDefault
                            ? "baseline"
                            : filter.isSwitch
                              ? "center"
                              : "normal"
                        }
                        justifyContent={"space-between"}
                      >
                        {setting.component}
                      </Grid>
                    ))}
                  </Grid>
                </IgAccordion>
              </Grid>
            )
        )}
    </Grid>
  );
};
